import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, Observable, of, tap, throwError } from 'rxjs';

import { CompareResponse, Product } from '../models/product.model';
import { ProductService } from './product.service';
import { ToastService } from './toast.service';
import { toUiProduct } from '../utils/product.utils';

const STORAGE_KEY = 'perronstore:compare:ids';
const MAX_ITEMS = 4;

@Injectable({ providedIn: 'root' })
export class CompareService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly productService = inject(ProductService);
  private readonly toastService = inject(ToastService);

  readonly maxItems = MAX_ITEMS;

  private readonly _ids = signal<number[]>([]);
  private readonly _items = signal<Product[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _missingIds = signal<number[]>([]);
  private readonly _unavailableIds = signal<number[]>([]);

  readonly ids = this._ids.asReadonly();
  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly missingIds = this._missingIds.asReadonly();
  readonly unavailableIds = this._unavailableIds.asReadonly();

  readonly count = computed(() => this._ids().length);
  readonly canAdd = computed(() => this._ids().length < MAX_ITEMS);
  readonly isFull = computed(() => this._ids().length >= MAX_ITEMS);

  constructor() {
    if (this.isBrowser) {
      this.loadFromStorage();
      if (this._ids().length > 0) {
        this.fetchDetails().subscribe({ error: () => { /* silencioso: no romper bootstrap si la API falla */ } });
      }
    }
  }

  isSelected(productId: number | string): boolean {
    const id = Number(productId);
    return this._ids().includes(id);
  }

  toggle(product: Product): void {
    const id = Number(product.id);
    if (!Number.isFinite(id)) return;

    if (this.isSelected(id)) {
      this.remove(id);
      this.toastService.show({
        type: 'info',
        message: 'Quitado del comparador',
      });
      return;
    }

    if (!this.canAdd()) {
      this.toastService.show({
        type: 'error',
        message: `Solo puedes comparar hasta ${MAX_ITEMS} productos`,
      });
      return;
    }

    this.add(product);
    this.toastService.show({
      type: 'success',
      message: `Añadido al comparador (${this.count()}/${MAX_ITEMS})`,
    });
  }

  /**
   * Variante silenciosa (sin toast) para el botón "X" de cada columna de la
   * tabla: el usuario ya está gestionando la selección, un toast por cada
   * clic es ruidoso.
   */
  removeById(productId: number | string): void {
    const id = Number(productId);
    if (!Number.isFinite(id)) return;
    this.remove(id);
  }

  clear(): void {
    this._ids.set([]);
    this._items.set([]);
    this._missingIds.set([]);
    this._unavailableIds.set([]);
    this._error.set(null);
    this.persist();
  }

  hydrateFromIds(ids: number[]): void {
    const clamped = Array.from(new Set(ids.filter((n) => Number.isFinite(n))))
      .slice(0, MAX_ITEMS);
    this._ids.set(clamped);
    this.persist();
  }

  fetchDetails(): Observable<CompareResponse> {
    const ids = this._ids();
    if (ids.length === 0) {
      this._items.set([]);
      this._missingIds.set([]);
      this._unavailableIds.set([]);
      this._error.set(null);
      return of({ data: [], missing_ids: [], unavailable_ids: [] });
    }
    this._loading.set(true);
    this._error.set(null);
    return this.productService.compareProducts(ids).pipe(
      tap((res) => {
        // Normaliza el shape API → UI: resuelve imageUrl desde `images`/`image`,
        // calcula price con IVA a partir de sale_price + taxes, rellena currency
        // y el resto de campos que espera la UI. Sin esto, la tabla pinta
        // "undefinedNaN" en los precios e imágenes rotas.
        const adapted = (res.data ?? []).map((p) => toUiProduct(p));
        this._items.set(adapted);
        this._missingIds.set(res.missing_ids ?? []);
        this._unavailableIds.set(res.unavailable_ids ?? []);
        this._loading.set(false);
      }),
      catchError((err) => {
        this._error.set('No se pudo cargar la comparación');
        this._loading.set(false);
        return throwError(() => err);
      }),
    );
  }

  private add(product: Product): void {
    const id = Number(product.id);
    this._ids.update((xs) => (xs.includes(id) ? xs : [...xs, id]));
    this._items.update((xs) =>
      xs.some((p) => Number(p.id) === id) ? xs : [...xs, product],
    );
    this.persist();
  }

  private remove(id: number): void {
    this._ids.update((xs) => xs.filter((x) => x !== id));
    this._items.update((xs) => xs.filter((p) => Number(p.id) !== id));
    this.persist();
  }

  private persist(): void {
    if (!this.isBrowser) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this._ids()));
    } catch {
      /* quota / modo privado: no fatal */
    }
  }

  private loadFromStorage(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const ids = parsed
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n))
        .slice(0, MAX_ITEMS);
      this._ids.set(ids);
    } catch {
      /* JSON inválido: ignorar */
    }
  }
}
